'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// Deep-import, not the barrel - see the note in use-guest-provisioning.ts: the
// `@naumu/shared` barrel pulls zod into every marketing route.
import { isImageType, isPreviewableType, resolveFileMimeType } from '@naumu/shared/types/attachment';

import { isHeicFile, transcodeHeicPreview } from '@naumu/ui/lib/media/image-preview';
import type { StagedAttachment } from '@naumu/ui/components/composer/staged-attachment';

/**
 * Local file staging for the landing composers (hero + CTA). Files are staged
 * as the in-app attachment chips with object-URL previews but are NOT uploaded —
 * the space isn't provisioned yet. On submit the raw `File`s (`getFiles()`) are
 * handed to provisioning, which uploads them into the freshly-created space.
 * Preview object-URLs are revoked on remove and on unmount.
 */
export const useStagedFiles = () => {
	const [stagedAttachments, setStagedAttachments] = useState<StagedAttachment[]>([]);

	// Latest-staged ref so unmount cleanup + getFiles read the current list
	// without re-creating the callbacks.
	const stagedRef = useRef(stagedAttachments);
	stagedRef.current = stagedAttachments;
	// A HEIC transcode can outlive the composer. Without this the late patch
	// would write an object URL into a dead state tree, past the cleanup below.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			for (const a of stagedRef.current) {
				if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
			}
		};
	}, []);

	const addFiles = useCallback((files: FileList | File[]) => {
		const arr = Array.from(files);
		if (arr.length === 0) return;
		const staged = arr.map((file): StagedAttachment => {
			// Fall back to the extension when the browser gives no type - an iPhone
			// HEIC often arrives with an empty `File.type` outside Safari.
			const fileType = resolveFileMimeType(file.name, file.type);
			// Images paint straight from the object URL, video gets a thumbnail, and
			// PDFs and text get an object URL so the pre-send lightbox can open
			// them; everything else shows the document icon — same as the in-app
			// attachment chip. HEIC is the one image with no URL here: a raw HEIC
			// blob paints in Safari only, so it is transcoded below.
			const previewUrl = isImageType(fileType)
				? isHeicFile(file)
					? undefined
					: URL.createObjectURL(file)
				: isPreviewableType(fileType)
					? URL.createObjectURL(file)
					: undefined;
			return {
				id: crypto.randomUUID(),
				fileName: file.name,
				fileType,
				fileSize: file.size,
				progress: 100,
				status: 'complete',
				previewUrl,
				file,
			};
		});
		setStagedAttachments((prev) => [...prev, ...staged]);

		// HEIC only: every other image already has its object URL. Patched by id
		// once the transcode lands, or revoked outright if the composer is gone or
		// the chip was already removed - nothing else holds the URL, so dropping
		// it without revoking leaks the blob.
		for (const att of staged) {
			if (!att.file || !isImageType(att.fileType) || !isHeicFile(att.file)) continue;
			void transcodeHeicPreview(att.file).then((preview) => {
				if (!mountedRef.current) {
					URL.revokeObjectURL(preview.url);
					return;
				}
				setStagedAttachments((prev) => {
					if (!prev.some((a) => a.id === att.id)) {
						URL.revokeObjectURL(preview.url);
						return prev;
					}
					return prev.map((a) => (a.id === att.id ? { ...a, previewUrl: preview.url } : a));
				});
			});
		}
	}, []);

	const removeAttachment = useCallback((id: string) => {
		setStagedAttachments((prev) => {
			const att = prev.find((a) => a.id === id);
			if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
			return prev.filter((a) => a.id !== id);
		});
	}, []);

	const clear = useCallback(() => {
		for (const a of stagedRef.current) {
			if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
		}
		setStagedAttachments([]);
	}, []);

	/** Raw File objects for the staged attachments — handed to provisioning. */
	const getFiles = useCallback(
		(): File[] => stagedRef.current.map((a) => a.file).filter((f): f is File => !!f),
		[],
	);

	return { stagedAttachments, addFiles, removeAttachment, clear, getFiles };
};
